import { useCallback, useEffect, useState } from 'react';

import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDate } from '@/lib/format';

import { type Grant, type GrantPage, type Group, PermissionsError, errorText, isSynced, permissionsApi } from './permissionsApi';
import { BackLink, Problem, givenBy, objectText, untilText, whoOf } from './shared';

const COLUMNS = [{ label: 'Group' }, { label: 'Owners', className: 'hidden md:table-cell' }, { label: 'Members' }, { label: 'Source' }];
/** A 403 on a synced group may be the sync rule or a missing right, so the text names both. Else the service's own text. */
export function writeRefusal(synced: boolean, failure: unknown): string {
  return synced && failure instanceof PermissionsError && failure.status === 403 ? 'This group is synced or you may not change it' : errorText(failure);
}

/** Only a person row with an id can be ended here. An agent, a nested group or an unrecorded row (no id) belongs to the service. */
export const canRemove = (g: Grant) => g.id !== '' && g.subject.startsWith('user:') && g.subject !== 'user:*';

/**
 * The text that stops a hand group name before the service sees it: a synced prefix (the service refuses it too, and its
 * own refusal shows if this check is ever behind it). Undefined when the name may go to the service.
 */
export const newGroupRefusal = (name: string): string | undefined =>
  isSynced({ name, synced: false }) ? 'The names that start with wos-, okta- or bhr- belong to synced groups. Choose another name.' : undefined;

const ownerText = (g: Group) => (g.owners.length ? g.owners.map((o) => o.label || 'Unknown person').join(', ') : '–');

export function GroupsTab({ open, onOpen }: { open?: string; onOpen: (id?: string) => void }) {
  const [groups, setGroups] = useState<Group[]>();
  const [error, setError] = useState<string>();
  const load = useCallback(async () => {
    setError(undefined);
    try {
      setGroups(await permissionsApi.groups());
    } catch (failure) {
      setError(errorText(failure));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const selected = open ? groups?.find((g) => g.name === open) : undefined;
  if (open && selected) return <GroupDetail group={selected} onBack={() => onOpen()} onChanged={() => void load()} />;

  return (
    <DataTable toolbar={<NewGroup onCreated={(name) => { void load(); onOpen(name); }} />}>
      <DataTableHead columns={COLUMNS} />
      {error ? (
        <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => void load()} />
      ) : !groups ? (
        <DataTableMessage colSpan={COLUMNS.length} loading>
          Loading groups
        </DataTableMessage>
      ) : groups.length === 0 ? (
        <DataTableMessage colSpan={COLUMNS.length}>No groups.</DataTableMessage>
      ) : (
        <tbody>
          {groups.map((g) => (
            <DataTableRow key={g.name} to={`?tab=groups&open=${encodeURIComponent(g.name)}`}>
              <DataTableCell>
                <button type="button" className="font-medium text-foreground hover:underline" onClick={() => onOpen(g.name)}>
                  {g.name}
                </button>
              </DataTableCell>
              <DataTableCell className="hidden text-muted-foreground md:table-cell">{ownerText(g)}</DataTableCell>
              <DataTableCell>{g.member_count}</DataTableCell>
              <DataTableCell>{isSynced(g) ? <StatusPill tone="neutral">Synced</StatusPill> : <span className="text-muted-foreground">–</span>}</DataTableCell>
            </DataTableRow>
          ))}
        </tbody>
      )}
    </DataTable>
  );
}

/** Makes a hand group. The service takes a name only, so there is no title. */
function NewGroup({ onCreated }: { onCreated: (name: string) => void }) {
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const clean = name.trim();
  const create = async () => {
    const refused = newGroupRefusal(clean);
    if (refused) return setProblem(refused);
    setBusy(true);
    try {
      await permissionsApi.createGroup(clean);
      setName('');
      setProblem(undefined);
      onCreated(clean);
    } catch (failure) {
      setProblem(errorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (clean && !busy) void create();
      }}
    >
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New group name, for example tt" aria-label="New group name" className="max-w-xs" />
      <Button type="submit" size="sm" variant="outline" disabled={!clean || busy}>
        Create group
      </Button>
      {problem ? <span role="alert" className="text-sm text-destructive">{problem}</span> : null}
    </form>
  );
}

/** A list of grants read page by page: Load more adds the next page. */
function usePagedGrants(read: (after?: string | null) => Promise<GrantPage>) {
  const [page, setPage] = useState<GrantPage>();
  const [rows, setRows] = useState<Grant[]>([]);
  const [error, setError] = useState<string>();
  const load = useCallback(
    async (after?: string | null) => {
      try {
        const next = await read(after);
        setPage(next);
        setRows((now) => (after ? [...now, ...next.grants] : next.grants));
        setError(undefined);
      } catch (failure) {
        setError(errorText(failure));
      }
    },
    [read],
  );
  useEffect(() => {
    void load();
  }, [load]);
  return { page, rows, error, load };
}

function MoreRow({ pages }: { pages: ReturnType<typeof usePagedGrants> }) {
  if (!pages.page?.next) return null;
  return (
    <div className="border-t border-border p-3">
      <Button type="button" size="sm" variant="outline" onClick={() => void pages.load(pages.page?.next)}>
        Load more
      </Button>
    </div>
  );
}

function GroupDetail({ group, onBack, onChanged }: { group: Group; onBack: () => void; onChanged: () => void }) {
  const synced = isSynced(group);
  const members = usePagedGrants(useCallback((after) => permissionsApi.grants('object', `group:${group.name}`, { after }), [group.name]));
  const uses = usePagedGrants(useCallback((after) => permissionsApi.groupGrants(group.name, after), [group.name]));
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState<string>();

  const change = async (key: string, job: () => Promise<void>) => {
    setBusy(key);
    setProblem(undefined);
    try {
      await job();
      setEmail('');
      setReason('');
      await members.load();
      onChanged();
    } catch (failure) {
      setProblem(writeRefusal(synced, failure));
    } finally {
      setBusy(undefined);
    }
  };
  const people = members.rows.filter((g) => g.relation === 'member');
  const owners = members.rows.filter((g) => g.relation === 'owner');

  return (
    <div className="space-y-6">
      <div>
        <BackLink label="All groups" onClick={onBack} />
        <h2 className="text-lg font-semibold">{group.name}</h2>
        {synced ? <p className="mt-1 text-sm text-muted-foreground">This group is synced from a source system. Its members change there, not here.</p> : null}
      </div>
      {problem ? <Problem message={problem} /> : null}
      <AdminSection title="Members" description={`${group.member_count} member${group.member_count === 1 ? '' : 's'}`} bodyClassName="p-0">
        {members.error ? <div className="p-4"><Problem message={members.error} onRetry={() => void members.load()} /></div> : null}
        {members.page && people.length === 0 && !members.error ? <p className="p-5 text-sm text-muted-foreground">No members.</p> : null}
        {people.length > 0 ? (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
              <tr>
                {['Person', 'Since', 'Given by', ''].map((label) => (
                  <th key={label} scope="col" className="px-4 py-2 font-medium">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {people.map((grant, i) => (
                <tr key={grant.id || `row-${i}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5">{whoOf(grant)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{formatDate(grant.created_at)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{givenBy(grant)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {synced ? null : !canRemove(grant) ? (
                      <span className="text-xs text-muted-foreground">added by the service</span>
                    ) : (
                      <Button type="button" size="sm" variant="outline" disabled={busy === grant.id} aria-label={`Remove member ${whoOf(grant)}`} onClick={() => void change(grant.id, () => permissionsApi.endGrant(grant.id))}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <MoreRow pages={members} />
        {synced ? null : (
          <form
            className="flex flex-wrap items-end gap-2 border-t border-border p-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim() && reason.trim()) void change('add', () => permissionsApi.addGroupMember(group.name, email.trim(), reason.trim()));
            }}
          >
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email of the new member" aria-label="New member email" className="max-w-xs" />
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" aria-label="Reason for the new member" className="max-w-xs" />
            <Button type="submit" size="sm" disabled={!email.trim() || !reason.trim() || busy === 'add'}>
              Add member
            </Button>
          </form>
        )}
      </AdminSection>
      <AdminSection title="Owners" bodyClassName="p-5">
        {owners.length === 0 ? <p className="text-sm text-muted-foreground">No owners.</p> : <p className="text-sm">{owners.map(whoOf).join(', ')}</p>}
      </AdminSection>
      <AdminSection title="What the group may use" bodyClassName="p-0">
        {uses.error ? <div className="p-4"><Problem message={uses.error} onRetry={() => void uses.load()} /></div> : null}
        {uses.page && uses.rows.length === 0 && !uses.error ? <p className="p-5 text-sm text-muted-foreground">The group holds no access.</p> : null}
        {uses.rows.length > 0 ? (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
              <tr>
                {['Access', 'Given by', 'Until', 'Reason'].map((label) => (
                  <th key={label} scope="col" className="px-4 py-2 font-medium">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {uses.rows.map((grant) => (
                <tr key={grant.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5">{objectText(grant.object)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{givenBy(grant)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{untilText(grant.ends_at)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{grant.reason || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <MoreRow pages={uses} />
      </AdminSection>
    </div>
  );
}
